/**
 * Quick filters: a choice of values for the few columns people slice a list by.
 *
 * WHY THIS EXISTS. The community DataGrid holds exactly ONE column condition,
 * so "level 3 AND subsystem structures" cannot be asked of it - and that is the
 * everyday question on a requirements list (issue #68). Issue #35 removed a
 * five-menu faceted bar because it duplicated what the column filter could do;
 * this is deliberately the part of that decision that turned out wrong. It is
 * NOT the bar that came out, though: that one filtered on the server and needed
 * /api/facets to fill its menus. This one is pure client-side logic over the
 * rows the screen already holds, in the same order every other filter runs:
 * search, then this, then the grid's one column condition.
 *
 * The semantics are the ones a person expects of a row of dropdowns. Choosing
 * several values in ONE field means "any of these"; choosing values in several
 * fields means "all of these fields agree". A field with nothing chosen filters
 * nothing.
 *
 * VALUES ARE STRINGS. A level arrives as the number 2, a URL carries '2', and
 * the control offers '2' - comparing them as anything else is how a pasted link
 * would select nothing. Everything goes through `String()` once, here.
 *
 * Pure on purpose, like rowFilter: an off-by-one in matching is invisible on
 * screen and obvious in a unit test.
 */

import { naturalCompare } from '../common/naturalCompare'

/** Field name -> the values chosen for it. A missing or empty entry means "any". */
export type QuickSelection = Record<string, string[]>

/**
 * The "value" of a row that has none.
 *
 * Offered as a choice (labelled "(none)" by the control) rather than ignored,
 * because "which requirements have no subsystem yet" is a question the list
 * should be able to answer, and a filter that cannot select the blanks leaves
 * them unreachable the moment anything else is chosen.
 */
export const NONE = ''

/**
 * The values a row has in a field, as strings.
 *
 * An ARRAY row value (a list field) has several, and matches when any is
 * chosen. An empty array, null, undefined and '' are all the same thing -
 * nothing stated - and are NONE, so an empty `verified_by` and a missing one
 * cannot end up as two different choices.
 */
export function valuesOf(row: object, field: string): string[] {
  const value: unknown = (row as Record<string, unknown>)[field]
  const list = Array.isArray(value) ? value : [value]
  const present = list
    .filter((item) => item !== null && item !== undefined && item !== '')
    .map(String)
  return present.length > 0 ? present : [NONE]
}

/** Is every one of these a finite number, in the way a level or a count is? */
function allNumeric(values: string[]): boolean {
  return values.every((value) => value.trim() !== '' && Number.isFinite(Number(value)))
}

/**
 * The distinct values present in a field, sorted, with NONE last.
 *
 * Numeric order when every value is a number, so level 10 follows level 9 and
 * not level 1; otherwise NATURAL, so a value that ends in a number (a subsystem
 * 'sub2', 'sub10') reads in order (issue #82). The comparison is pinned to 'en'
 * rather than left to the browser's own locale: a list that sorts differently on
 * two machines makes a link read differently to two people.
 */
export function quickOptions(rows: object[], field: string): string[] {
  const seen = new Set<string>()
  for (const row of rows) {
    for (const value of valuesOf(row, field)) seen.add(value)
  }
  const named = [...seen].filter((value) => value !== NONE)
  named.sort(
    allNumeric(named)
      ? (a, b) => Number(a) - Number(b)
      : naturalCompare,
  )
  return seen.has(NONE) ? [...named, NONE] : named
}

/**
 * How many rows have each value in a field.
 *
 * Counts ROWS, not occurrences: a row listing the same value twice counts once,
 * so the number says what choosing the value would show.
 */
export function quickCounts(rows: object[], field: string): Map<string, number> {
  const counts = new Map<string, number>()
  for (const row of rows) {
    for (const value of new Set(valuesOf(row, field))) {
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
  }
  return counts
}

/**
 * The selection with one field's choices replaced; none removes the field.
 *
 * A copy, never a mutation, and the field is DROPPED rather than kept as an
 * empty list - an entry left behind for every field somebody once touched is
 * how a selection that looks empty compares unequal to one that is.
 */
export function withChoices(
  selection: QuickSelection,
  field: string,
  chosen: string[],
): QuickSelection {
  const others = Object.entries(selection).filter(([name]) => name !== field)
  return Object.fromEntries(chosen.length > 0 ? [...others, [field, chosen]] : others)
}

/** Whether anything is chosen at all. */
export function hasQuick(selection: QuickSelection): boolean {
  return Object.values(selection).some((chosen) => chosen.length > 0)
}

/**
 * The rows that satisfy the selection: any chosen value within a field, every
 * field with a choice across fields.
 *
 * Returns the SAME array when nothing is chosen, so a caller that memoizes on
 * the result does not re-render for a filter that is not there. A chosen value
 * no row has simply matches nothing - a stale link is an empty list that says
 * so, not an error.
 */
export function applyQuick<T extends object>(rows: T[], selection: QuickSelection): T[] {
  const active = Object.entries(selection).filter(([, chosen]) => chosen.length > 0)
  if (active.length === 0) return rows
  return rows.filter((row) =>
    active.every(([field, chosen]) => valuesOf(row, field).some((value) => chosen.includes(value))),
  )
}

/**
 * The query-string key the selection travels under.
 *
 * ONE key holding JSON, rather than a key per field, for the same reason the
 * column filters are one `filter`: the fields are whatever the screen offers,
 * and a key per field would make this module (or the router) know the list.
 */
export const QUICK_KEY = 'quick'

/**
 * Read the selection out of a URL query string.
 *
 * **Defensive, for the reason `readFilterModel` is.** A query string is
 * hand-editable and outlives a redeploy, and a link pasted into an issue last
 * month must never crash the page it opens. Anything that is not an object of
 * lists reads as no selection; inside a list, only strings and numbers are kept
 * (numbers because somebody hand-writing `{"level":[2]}` means level 2).
 *
 * Built with `Object.fromEntries` rather than by assigning to a fresh object,
 * because a key of `__proto__` assigned to a plain object changes its prototype
 * instead of adding an entry.
 */
export function readQuick(params: URLSearchParams): QuickSelection {
  const raw = params.get(QUICK_KEY)
  if (!raw) return {}

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}

    const kept = Object.entries(parsed as Record<string, unknown>)
      .map(([field, chosen]): [string, string[]] => [
        field,
        Array.isArray(chosen)
          ? [
              ...new Set(
                chosen
                  .filter((item) => typeof item === 'string' || typeof item === 'number')
                  .map(String),
              ),
            ]
          : [],
      ])
      .filter(([, chosen]) => chosen.length > 0)
    return Object.fromEntries(kept)
  } catch {
    return {}
  }
}

/**
 * Write the selection back into a query string.
 *
 * Empty entries are omitted and an entirely empty selection removes the key, so
 * the URL of an unfiltered list is the bare path and the link somebody shares
 * says what they mean. Other keys are preserved: this owns one key, not the
 * query string.
 */
export function writeQuick(params: URLSearchParams, selection: QuickSelection): URLSearchParams {
  const next = new URLSearchParams(params)
  const kept = Object.entries(selection).filter(([, chosen]) => chosen.length > 0)
  if (kept.length === 0) next.delete(QUICK_KEY)
  else next.set(QUICK_KEY, JSON.stringify(Object.fromEntries(kept)))
  return next
}
